import { lightDark, SeasonalTokenOverrides, SeasonConfig } from '../types';
import { DynamicBackground } from './dynamic-background';
import { FallingPetals } from './falling-petals';
import SpringLogoDark from './images/logo-dark.png';
import SpringLogoLight from './images/logo-light.png';
import springWreathSvg from './images/wreath.svg';
import SpringAmbientSound from './sounds/spring-ambiance.mp3';

/**
 * Spring Theme - Semantic Token Overrides
 *
 * Each token provides a { default (light), _dark } pair.
 * Light values are used during daytime (6 AM – 7 PM),
 * dark values during nighttime (7 PM – 6 AM).
 */
const springTokenOverrides: SeasonalTokenOverrides = {
	colors: {
		primary: {
			fg: lightDark('#059669', '#34d399'),
			fgStrong: lightDark('#047857', '#10b981'),
			fgStronger: lightDark('#065f46', '#059669'),
			fgStrongest: lightDark('#064e3b', '#047857'),
			bg: lightDark('#059669', '#10b981'),
			bgHover: lightDark('#047857', '#059669'),
			bgActive: lightDark('#065f46', '#047857'),
			bgAlpha: lightDark('rgba(5, 150, 105, 0.12)', 'rgba(52, 211, 153, 0.15)'),
			bgAlphaHover: lightDark('rgba(5, 150, 105, 0.20)', 'rgba(52, 211, 153, 0.25)'),
			bgAlphaActive: lightDark('rgba(5, 150, 105, 0.28)', 'rgba(52, 211, 153, 0.35)'),
		},
		bg: {
			canvas: lightDark('#faf9f6', 'transparent'),
			surface: lightDark('#ffffff', '#1e1b2e'),
			soft: lightDark('#f0fdf4', '#2a2640'),
			subtle: lightDark('#f5f3ef', '#231f38'),
			topNav: lightDark('#ffffff', '#16132a'),
			inset: lightDark('#ecfdf5', '#13112a'),
		},
		text: {
			interactive: lightDark('#059669', '#34d399'),
		},
		border: {
			default: lightDark('#d1d5db', '#3b3558'),
			soft: lightDark('#e5e7eb', '#2a2640'),
		},
	},
};

export const springConfig: SeasonConfig = {
	themeClassName: 'spring-theme',
	toggleEmoji: '🌸',

	colorModeStrategy: 'time-based',
	semanticTokenOverrides: springTokenOverrides,

	logoLight: SpringLogoLight,
	logoDark: SpringLogoDark,

	modalSound: SpringAmbientSound,
	modalSoundVolume: 0.1,

	buttonTexts: [
		'🌸 Bloom some praise',
		'🌱 Grow some gratitude',
		'🦋 Spread appreciation',
		'🌷 Plant some kudos',
		'☀️ Brighten their day',
		'🌻 Send sunshine',
		'🐝 Buzz with recognition',
		'🌈 Share the warmth',
	],

	modalDecorations: [
		{ emoji: '🌷', bottom: '40px', left: '30px', fontSize: '25px', opacity: 0.15, rotation: -10 },
		{ emoji: '🌱', top: '60px', left: '60%', fontSize: '20px', opacity: 0.1, rotation: 25 },
		{ emoji: '☀️', bottom: '60px', right: '40px', fontSize: '22px', opacity: 0.12, rotation: 12 },
	],

	modalColors: {
		logoOverride: 'light',
		logoMaxWidth: '360px',
		overlayBg: 'blackAlpha.600',
		contentBg: '#faf9f6',
		borderColor: 'green.300',
		boxShadow: '0 20px 60px rgba(5, 150, 105, 0.2)',
		logoDropShadowColor: 'transparent',
		subtitleColor: 'green.700',
		enableButtonBg: 'green.600',
		enableButtonHoverBg: 'green.500',
		enableButtonActiveBg: 'green.700',
		enableButtonBoxShadow: '0 4px 15px rgba(5, 150, 105, 0.3)',
		enableButtonHoverBoxShadow: '0 8px 25px rgba(5, 150, 105, 0.4)',
		enableButtonTextColor: 'white',
		cancelButtonColor: 'green.600',
		cancelButtonHoverColor: 'green.500',
		noticeBg: 'green.50',
		noticeBorderColor: 'green.200',
		noticeTextColor: 'green.700',
		featureCardBg: 'green.50',
		featureCardBorderColor: 'green.100',
		featureCardTextColor: 'gray.700',
		closeButtonHoverBg: 'blackAlpha.100',
	},

	modalText: {
		subtitle: 'Bring your Bonusly to life with our spring theme!',
		features: [
			{
				emoji: '🌸',
				text: 'Cherry blossom petals drift gently across your screen',
			},
			{
				emoji: '🌗',
				text: 'Colors shift naturally throughout the day — light by day, dark by night',
			},
			{
				emoji: '🌈',
				text: 'A fresh spring palette with warm greens and blooming accents',
			},
		],
		endDateNotice: 'This spring theme will be available through the season. Toggle it on/off anytime!',
		enableButtonText: 'Enable Spring Theme',
		cancelButtonText: 'Maybe Later',
	},

	EffectsComponent: () => (
		<>
			<DynamicBackground />
			<FallingPetals />
		</>
	),

	avatarOverlayImage: springWreathSvg,
	/** Slightly smaller + nudge down vs default 1.4 — spring wreath is taller (flowers) than winter */
	avatarOverlayScale: 1.28,
	avatarOverlayTranslateY: '3px',
};
